<template>
    <div
        class="s2w-workspace-item"
        :class="{
            'is-current': selectable && isCurrentWorkspace,
            'is-editing': editable && workspace.isEditing,
        }"
    >
        <div
            ref="workspaceInfoRef"
            class="s2w-workspace-item-info"
            :data-space-id="workspace.space_id"
            @click="selectWorkspace(workspace)"
        >
            <PtIcon
                class="workspace-icon"
                style="font-size: 24px;"
                :icon="service.getWorkspaceIcon(workspace)"
            />
            <div class="workspace-name-container">
                <div
                    v-if="editable && workspace.isEditing"
                    class="workspace-name-editor"
                    @click.stop
                >
                    <!-- eslint-disable vue/no-mutating-props -->
                    <input
                        ref="workspaceNameInputRef"
                        v-model="workspace.space_name"
                        :class="{
                            error: !service.isWorkspaceNameValid(workspace)
                        }"
                        type="text"
                        maxlength="80"
                        :disabled="workspace.isUpdating"
                        @keypress.enter="onKeypressEnter"
                        @blur="() => finishEditWorkspace(workspace)"
                    >
                </div>
                <template v-else>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="workspace-name"
                        :title="titleText"
                        @dblclick="() => editWorkspace(workspace)"
                        v-html="service.renderSpaceName(workspace)"
                    />
                    <div
                        :title="titleText"
                        class="workspace-elements-count"
                    >
                        {{ countText }}
                    </div>
                </template>
            </div>
            <div
                v-if="editable"
                class="workspace-item-action-pane"
            >
                <PtIcon
                    v-if="service.hasEditWorkspacePermission(workspace)"
                    class="workspace-item-action-item"
                    style="font-size: 24px;"
                    icon="SolidEdit"
                    @click.stop="service.editWorkspace(workspace)"
                />
                <PtIcon
                    v-if="service.hasAddWorkspaceFolderPermission(workspace)"
                    class="workspace-item-action-item"
                    style="font-size: 24px;"
                    icon="SolidFolderAdd"
                    @click.stop="addFolder(workspace)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import get from 'lodash/get';
import assign from 'lodash/assign';
import PtIcon from '@patsnap-biz/icon';
import '@patsnap-ui/icon/assets/solid/SpaceShared.svg';
import '@patsnap-ui/icon/assets/solid/company.svg';
import '@patsnap-ui/icon/assets/solid/3d.svg';
import '@patsnap-ui/icon/assets/solid/edit.svg';
import '@patsnap-ui/icon/assets/solid/FolderAdd.svg';
import { scrollIntoViewIfNeeded } from '../../utils';

export default {
    name: 'SaveToWorkspaceWorkspaceItem',
    components: {
        PtIcon,
    },
    inject: ['service'],
    props: {
        workspace: {
            type: Object,
            required: true,
        },
        selectable: {
            type: Boolean,
            default: true,
        },
        editable: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isCurrentWorkspace() {
            return get(this.service.folderSelector.selectedWorkspace, 'space_id') === get(this.workspace, 'space_id');
        },
        countText() {
            return this.service.getWorkspaceElementsCountString(this.workspace);
        },
        titleText() {
            const { workspace, countText } = this;
            return `${workspace.space_name} ${countText}`;
        },
        isWorkspaceNameError() {
            return true;
        },
    },
    watch: {
        'workspace.isEditing': {
            handler() {
                this.$nextTick(() => {
                    const workspaceNameInputRef = get(this, '$refs.workspaceNameInputRef');
                    if (workspaceNameInputRef) {
                        workspaceNameInputRef.focus();
                        workspaceNameInputRef.select();
                    }
                });
            },
            immediate: true,
        },
    },
    mounted() {
        this.$nextTick(() => {
            const { folderSelector } = this.service;
            if (folderSelector.initWorkspaceScroll || this.workspace !== folderSelector.selectedWorkspace) {
                return false;
            }
            folderSelector.initWorkspaceScroll = true;
            scrollIntoViewIfNeeded(this.$el);
            return true;
        });
    },
    methods: {
        resetWorkspaceName() {
            assign(this.workspace, {
                space_name: this.workspace.originSpaceName,
            });
            // eslint-disable-next-line vue/no-mutating-props
            this.workspace.isEditing = false;
        },
        editWorkspace(workspace) {
            if (this.editable) {
                this.service.editWorkspace(workspace);
            }
        },
        async finishEditWorkspace(workspace) {
            await this.service.finishEditWorkspace(workspace);
            if (workspace.isEditing) {
                this.resetWorkspaceName();
            }
        },
        onKeypressEnter() {
            const workspaceNameInputRef = get(this, '$refs.workspaceNameInputRef');
            if (workspaceNameInputRef) {
                workspaceNameInputRef.blur();
            }
        },
        selectWorkspace(workspace) {
            return this.service.selectWorkspace(workspace).then(() => {
                this.service.selectFolder(get(workspace, 'folderTreeData[0]'));
            }).catch(() => {});
        },
        async addFolder(workspace) {
            if (!this.isCurrentWorkspace) {
                await this.selectWorkspace(workspace);
            }
            setTimeout(() => {
                this.service.addFolder({
                    workspace,
                    parentFolder: null,
                });
            }, 100);
        },
    },
};
</script>
